import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import emailjs from '@emailjs/browser';
import {
  Mail, ArrowRight, Search, FileText, Settings, Rocket,
  MessageSquare, Shield, Clock, Zap, CheckCircle2, Bot, Check,
} from 'lucide-react';
import TechPattern from '../components/shared/TechPattern';

const steps = [
  { step: '01', title: 'Free Diagnostic', desc: 'We map your operations and identify where AI agents generate the most impact.', color: '#2563eb', icon: Search },
  { step: '02', title: 'Live Demo', desc: 'See engineers and AI agents working together in a real scenario.', color: '#0891b2', icon: FileText },
  { step: '03', title: 'Implementation', desc: "We deploy your AI team in your real environment. Setup in minutes, results from day one.", color: '#ea580c', icon: Settings },
  { step: '04', title: 'Continuous Operations', desc: 'Your AI team operates 24/7, evolves with your business, and delivers results.', color: '#7c3aed', icon: Rocket },
];

const faqs = [
  {
    q: 'How much does it cost?',
    a: "It depends on the size of the operation and the team composition. On the demo call we'll show you the numbers for your specific scenario.",
  },
  {
    q: "AI doesn't deliver with enough quality.",
    a: "Agreed that AI alone isn't reliable. That's why our engineers work alongside AI agents. They validate, review, and ensure quality. You get objective accuracy metrics.",
  },
  {
    q: 'Is it secure? Where does our data go?',
    a: "Each company has a completely isolated environment. Data never mixes between clients. Access uses only the permissions you define. We have audit logs of everything.",
  },
  {
    q: "My team won't accept it.",
    a: 'What teams reject is the abstract idea of "AI replacing people." What teams accept is "I don\'t have to do 15 reviews a week anymore." In practice, when AI takes over repetitive work, the team is grateful.',
  },
  {
    q: "We're in a cost-cutting phase.",
    a: "Exactly. If you're reducing headcount and need to maintain the same output, this is precisely the scenario where AI teams pay for themselves fastest.",
  },
  {
    q: 'We already have something internal.',
    a: "Good sign you're already on this path. Can we ask: who maintains it? How much time per week goes into it? In many cases, the cost of maintaining a homegrown solution is higher than using a product that evolves on its own.",
  },
];

const ContactPage: React.FC = () => {
  const formRef = useRef<HTMLFormElement>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    teamSize: '',
    message: '',
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email) {
      setError('Please fill in your name and email.');
      return;
    }
    setSending(true);
    setError('');

    try {
      await emailjs.send(
        'service_2az1as8',
        'template_l5uccn8',
        {
          from_name: formData.name,
          from_email: formData.email,
          company: formData.company,
          team_size: formData.teamSize,
          message: formData.message,
        },
        'ooZezC0ow70jYQcQd'
      );
      setSent(true);
      setFormData({ name: '', email: '', company: '', teamSize: '', message: '' });
    } catch {
      setError('Something went wrong. Please try again or email us at aiteam.suporte@gmail.com');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Hero */}
      <section style={{
        paddingTop: 160, paddingBottom: 60, position: 'relative', overflow: 'hidden',
        background: 'var(--bg-secondary)',
      }}>
        <TechPattern variant="waves" opacity={0.04} />
        <div className="gradient-mesh" />
        <div className="container" style={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span className="section-label"><Mail size={14} />Get Started</span>
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            style={{ fontSize: 'clamp(2.2rem, 5vw, 3.8rem)', fontWeight: 900, lineHeight: 1.1, marginBottom: 20 }}
          >
            Schedule Your <span className="gradient-text">Free Diagnostic</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            style={{ fontSize: '1.15rem', color: 'var(--text-secondary)', maxWidth: 600, margin: '0 auto', lineHeight: 1.7 }}
          >
            Tell us about your team and we'll show you exactly where AI can
            generate the most impact. No commitment required.
          </motion.p>
        </div>
      </section>

      {/* Form + Sidebar */}
      <section className="section" style={{ paddingTop: 0 }}>
        <div className="container">
          <div style={{
            display: 'grid', gridTemplateColumns: '1.2fr 0.8fr', gap: 48, maxWidth: 1000, margin: '-40px auto 0',
          }}>
            {/* Form */}
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }}
              style={{
                background: '#ffffff', borderRadius: 'var(--radius-lg)', padding: '40px 32px',
                border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-card)',
              }}
            >
              {sent ? (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                  <div style={{
                    width: 64, height: 64, borderRadius: 20,
                    background: '#ecfdf5', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    margin: '0 auto 20px',
                  }}>
                    <Check size={32} color="#059669" />
                  </div>
                  <h3 style={{ fontSize: '1.3rem', fontWeight: 700, marginBottom: 8 }}>Request Sent!</h3>
                  <p style={{ fontSize: '0.95rem', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                    We'll get back to you within 24 hours to schedule your free diagnostic.
                  </p>
                </div>
              ) : (
                <>
                  <h3 style={{ fontSize: '1.2rem', fontWeight: 700, marginBottom: 32 }}>Request a Demo</h3>
                  <form ref={formRef} onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                    {[
                      { name: 'name', label: 'Full Name', type: 'text', placeholder: 'John Smith' },
                      { name: 'email', label: 'Work Email', type: 'email', placeholder: 'john@company.com' },
                      { name: 'company', label: 'Company Name', type: 'text', placeholder: 'Acme Corp' },
                    ].map((field) => (
                      <div key={field.name}>
                        <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>
                          {field.label}
                        </label>
                        <input
                          type={field.type} name={field.name} placeholder={field.placeholder}
                          value={formData[field.name as keyof typeof formData]} onChange={handleChange}
                          style={{
                            width: '100%', padding: '14px 16px', background: 'var(--bg-secondary)',
                            border: '1px solid var(--border-color)', borderRadius: 12,
                            color: 'var(--text-primary)', fontSize: '0.95rem', outline: 'none',
                            transition: 'border-color 0.2s', fontFamily: 'inherit',
                          }}
                          onFocus={(e) => { e.currentTarget.style.borderColor = '#2563eb'; }}
                          onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--border-color)'; }}
                        />
                      </div>
                    ))}

                    <div>
                      <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>
                        Team Size
                      </label>
                      <select name="teamSize" value={formData.teamSize} onChange={handleChange}
                        style={{
                          width: '100%', padding: '14px 16px', background: 'var(--bg-secondary)',
                          border: '1px solid var(--border-color)', borderRadius: 12,
                          color: formData.teamSize ? 'var(--text-primary)' : 'var(--text-muted)',
                          fontSize: '0.95rem', outline: 'none', fontFamily: 'inherit', cursor: 'pointer',
                        }}
                      >
                        <option value="">Select team size</option>
                        <option value="5-20">5-20 people</option>
                        <option value="20-50">20-50 people</option>
                        <option value="50-200">50-200 people</option>
                        <option value="200-500">200-500 people</option>
                        <option value="500+">500+ people</option>
                      </select>
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>
                        What's your biggest operational challenge?
                      </label>
                      <textarea
                        name="message" placeholder="Tell us about the repetitive tasks, bottlenecks, or areas where your team loses the most time..."
                        value={formData.message} onChange={handleChange} rows={4}
                        style={{
                          width: '100%', padding: '14px 16px', background: 'var(--bg-secondary)',
                          border: '1px solid var(--border-color)', borderRadius: 12,
                          color: 'var(--text-primary)', fontSize: '0.95rem', outline: 'none',
                          fontFamily: 'inherit', resize: 'vertical',
                        }}
                        onFocus={(e) => { e.currentTarget.style.borderColor = '#2563eb'; }}
                        onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--border-color)'; }}
                      />
                    </div>

                    {error && (
                      <p style={{ fontSize: '0.85rem', color: '#dc2626', margin: 0 }}>{error}</p>
                    )}

                    <button type="submit" disabled={sending}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                        padding: '16px 32px', background: sending ? '#94a3b8' : 'var(--gradient-blue)',
                        borderRadius: 12, fontSize: '1rem', fontWeight: 600, color: 'white',
                        cursor: sending ? 'not-allowed' : 'pointer',
                        transition: 'transform 0.2s, box-shadow 0.2s', border: 'none',
                      }}
                      onMouseEnter={(e) => { if (!sending) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 30px rgba(37, 99, 235, 0.4)'; } }}
                      onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                    >
                      {sending ? 'Sending...' : 'Schedule Free Diagnostic'} {!sending && <ArrowRight size={18} />}
                    </button>
                  </form>
                </>
              )}
            </motion.div>

            {/* Sidebar */}
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 }}
              style={{ display: 'flex', flexDirection: 'column', gap: 24 }}
            >
              <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 'var(--radius-lg)', padding: '28px 24px' }}>
                <h4 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 16, color: '#059669' }}>What to Expect</h4>
                {[
                  { icon: MessageSquare, text: '15-minute discovery call' },
                  { icon: Search, text: 'Operations diagnostic' },
                  { icon: Bot, text: 'Live demo with real AI team' },
                  { icon: Clock, text: 'ROI projection for your scenario' },
                ].map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: i < 3 ? 12 : 0 }}>
                      <Icon size={16} color="#059669" />
                      <span style={{ fontSize: '0.88rem', color: 'var(--text-secondary)' }}>{item.text}</span>
                    </div>
                  );
                })}
              </div>

              <div style={{ background: '#ffffff', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-lg)', padding: '28px 24px', boxShadow: 'var(--shadow-card)' }}>
                <h4 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 16 }}>Why AI Team?</h4>
                {[
                  { icon: Zap, text: 'Setup in minutes, not months', color: '#ea580c' },
                  { icon: Shield, text: '100% isolated, secure environment', color: '#059669' },
                  { icon: CheckCircle2, text: 'Free diagnostic included', color: '#2563eb' },
                  { icon: Clock, text: '24/7 operation, zero downtime', color: '#7c3aed' },
                ].map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: i < 3 ? 12 : 0 }}>
                      <Icon size={16} color={item.color} />
                      <span style={{ fontSize: '0.88rem', color: 'var(--text-secondary)' }}>{item.text}</span>
                    </div>
                  );
                })}
              </div>

              <div style={{ padding: '20px 24px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 'var(--radius)', textAlign: 'center' }}>
                <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                  <strong style={{ color: '#2563eb' }}>In cost-cutting mode?</strong><br />
                  That's exactly when AI teams pay for themselves fastest.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
        <style>{`@media (max-width: 768px) { section > .container > div[style*="grid-template-columns: 1.2fr"] { grid-template-columns: 1fr !important; } }`}</style>
      </section>

      {/* How it works */}
      <section className="section" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} style={{ textAlign: 'center', marginBottom: 48 }}>
            <h2 className="section-title" style={{ margin: '0 auto 16px' }}>How Getting Started <span className="gradient-text">Works</span></h2>
          </motion.div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20 }}>
            {steps.map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }} whileHover={{ y: -6 }}
                  style={{
                    background: '#ffffff', borderRadius: 'var(--radius)', padding: '28px 20px',
                    border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-card)',
                    textAlign: 'center', position: 'relative',
                  }}
                >
                  <div style={{ fontSize: '2.5rem', fontWeight: 800, fontFamily: 'Space Grotesk', color: item.color, opacity: 0.15, position: 'absolute', top: 12, right: 16 }}>{item.step}</div>
                  <div style={{ width: 52, height: 52, borderRadius: 14, background: `${item.color}0a`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                    <Icon size={24} color={item.color} />
                  </div>
                  <h4 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 8 }}>{item.title}</h4>
                  <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>{item.desc}</p>
                  {i < 3 && (
                    <div style={{ position: 'absolute', right: -14, top: '50%', transform: 'translateY(-50%)', zIndex: 1, color: 'var(--accent-blue)', opacity: 0.2 }}>
                      <ArrowRight size={18} />
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
        <style>{`@media (max-width: 768px) { section > .container > div[style*="grid-template-columns: repeat(4"] { grid-template-columns: repeat(2, 1fr) !important; } }`}</style>
      </section>

      {/* FAQ */}
      <section className="section">
        <div className="container" style={{ maxWidth: 800 }}>
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} style={{ textAlign: 'center', marginBottom: 48 }}>
            <h2 className="section-title" style={{ margin: '0 auto 16px' }}>Common <span className="gradient-text-cyan">Questions</span></h2>
          </motion.div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {faqs.map((faq, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                style={{ background: '#ffffff', borderRadius: 'var(--radius)', padding: '24px 28px', border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-card)' }}
              >
                <h4 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 12, color: '#2563eb' }}>"{faq.q}"</h4>
                <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', lineHeight: 1.7 }}>{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default ContactPage;
